import { http } from './request'
import api from './api'
import type { User, UserQueryParams, UserListResponse, UpdateUserParams } from '@/types/user'

export type AddUserParams = Partial<User> & {
  password?: string
}

/**
 * 用户登录
 */
export function login(data: { username: string; password: string }) {
  return http.postJSON(api.account.login, data)
}

export function loginBySms(data: {
  phone: string
  verifyCode: string
  agreeTerms: boolean
  agreePrivacy: boolean
  termsVersion: string
  privacyVersion: string
}) {
  return http.post(api.account.loginSms, {
    ...data,
    clientType: 'web',
  })
}

/**
 * 统一账号中心退出登录
 */
export function logoutAccount() {
  return http.post(api.account.logout)
}

/**
 * 用户注册
 */
export function register(data: {
  name: string
  username: string
  email: string
  password: string
  confirmPassword: string
  verifyCode: string
  agreeTerms: boolean
  agreePrivacy: boolean
  termsVersion: string
  privacyVersion: string
}) {
  return http.post(api.account.register, { ...data, clientType: 'web' })
}

export interface AccountPolicy {
  type: 'terms' | 'privacy'
  title?: string
  version: string
  content: string
  contentHtml?: string
  updatedAt: string
  publicUrl?: string
  id?: number
  isCurrent?: boolean
  publishedBy?: number
  createdAt?: string
}

export function getAccountPolicy(type: 'terms' | 'privacy') {
  return http.get<AccountPolicy>(`/account/policies/${type}`)
}

export function getAdminAccountPolicy(type: 'terms' | 'privacy') {
  return http.get<AccountPolicy>(`/account/admin/policies/${type}`)
}

export function getAdminAccountPolicyHistory(type: 'terms' | 'privacy') {
  return http.get<AccountPolicy[]>(`/account/admin/policies/${type}/history`)
}

export function publishAdminAccountPolicy(
  type: 'terms' | 'privacy',
  data: { title: string; contentHtml: string }
) {
  return http.put<AccountPolicy>(`/account/admin/policies/${type}`, data)
}

export function registerByPhone(data: {
  phone: string
  password: string
  confirmPassword: string
  verifyCode: string
  agreeTerms: boolean
  agreePrivacy: boolean
  termsVersion: string
  privacyVersion: string
}) {
  return http.post(api.account.registerPhone, {
    ...data,
    clientType: 'web',
  })
}

/**
 * 重置密码（通过邮箱验证码）
 */
export function resetPassword(data: { email: string; code: string; password: string }) {
  return http.post(api.account.passwordReset, {
    email: data.email,
    verifyCode: data.code,
    newPassword: data.password,
  })
}

/**
 * 修改密码（当前用户）
 */
export function changePassword(data: { oldPassword: string; newPassword: string }) {
  return http.postJSON(api.account.passwordChange, data)
}

/**
 * 发送邮箱验证码
 */
export function sendEmailCaptcha(data: { email: string; type: string }) {
  return http.post(api.verification.send, {
    app: 'afsa-web',
    scene: data.type,
    channel: 'EMAIL',
    target: data.email,
  })
}

export function sendSmsCaptcha(data: { phone: string; type: 'login' | 'register' }) {
  return http.post(api.verification.send, {
    app: 'afsa-web',
    scene: data.type,
    channel: 'SMS',
    target: data.phone,
  })
}

export function getVerificationCapabilities() {
  return http.get<{
    sms: {
      available: boolean
      codeLength: number
      expiresInSeconds: number
      cooldownSeconds: number
    }
  }>(api.verification.capabilities)
}

/**
 * 验证验证码
 */
export function checkCaptcha(data: { email: string; code: string; type: string }) {
  return http.post(api.verification.check, {
    channel: 'EMAIL',
    target: data.email,
    code: data.code,
  })
}

/**
 * 查询用户列表
 */
export function queryUsers(params: UserQueryParams) {
  return http.get<UserListResponse>(api.user.queryUsers, params)
}

/**
 * 更新用户信息
 */
export function updateUser(data: UpdateUserParams) {
  if (data.userId) {
    return http.postJSON(api.user.update, data as Record<string, unknown>)
  }
  return http.put('/account/profile', data as Record<string, unknown>)
}

/**
 * 添加用户
 */
export function addUser(data: AddUserParams) {
  return http.post(api.account.adminUsers, {
    username: data.username,
    password: data.password,
    name: data.name,
    email: data.email,
    tel: data.tel,
    isAdmin: data.isAdmin,
    state: data.state,
  })
}

/**
 * 删除用户
 */
export function deleteUser(userId: number) {
  return http.post(api.user.delete, { userId })
}

/**
 * 切换用户状态
 */
export function changeUserState(userId: number, state: string) {
  return http.post(api.user.changeState, { userId, state })
}

/**
 * 管理员重置用户密码
 */
export function adminResetPassword(userId: number) {
  return http.post(api.user.resetPassword, { userId })
}

/**
 * 查询指定用户的角色ID列表
 */
export function getUserRoleIds(userId: number) {
  return http.get<number[]>(api.user.getUserRoles.replace('{userId}', String(userId)))
}

/**
 * 为指定用户分配角色
 */
export function assignUserRoles(userId: number, roleIds: number[]) {
  return http.post(api.user.assignRoles, { userId, roleIds })
}

/**
 * 获取OAuth绑定状态
 */
export function getOauthBindings() {
  return http.get(api.user.oauthBindings)
}

/**
 * 绑定微信
 */
export function bindWechat(code: string) {
  return http.post(api.user.bindWechat, { code })
}

/**
 * 解绑微信
 */
export function unbindWechat() {
  return http.post(api.user.unbindWechat)
}

// ==================== 统一账号中心 / newapi 绑定 ====================

export interface AccountNewApiBinding {
  id?: number
  userId?: number
  newApiUserId?: string
  newApiUsername?: string
  newApiEmail?: string
  newApiMobile?: string
  externalUserId?: string
  bindMethod?: string
  status?: 'linked' | 'unlinked' | 'error' | string
  metadata?: string
  linkedAt?: string
  lastSyncedAt?: string
  createdAt?: string
  updatedAt?: string
}

export interface DesktopIntegrationExternalStatus {
  status?: 'linked' | 'not_linked' | 'error' | string
  displayName?: string
  message?: string
  lastSyncedAt?: string
}

export interface DesktopIntegrationBootstrap {
  newapi?: DesktopIntegrationExternalStatus
  channelModels?: Array<{
    id?: string
    name?: string
    provider?: string
    model?: string
    baseUrl?: string
  }>
  syncedAt?: string
}

export function getAccountNewApiBinding() {
  return http.get<AccountNewApiBinding | null>('/account/newapi-binding')
}

export function unlinkAccountNewApiBinding() {
  return http.post<AccountNewApiBinding | null>('/account/newapi-binding/unlink')
}

export function syncDesktopIntegrations() {
  return http.post<DesktopIntegrationBootstrap>('/desktop/integrations/sync')
}

// ==================== API 密钥管理 ====================

/**
 * API密钥信息
 */
export interface ApiKeyInfo {
  id: string
  name: string
  key?: string
  isActive: boolean
  createdAt: string
  expiresAt?: string
}

/**
 * 获取用户API密钥列表
 */
export function getUserApiKeys() {
  return http.get<ApiKeyInfo[]>('/api-keys')
}

/**
 * 创建API密钥
 */
export function createApiKey(data: { name: string; expiresInDays?: number }) {
  return http.post<{ key: string; name: string }>('/api-keys', data)
}

/**
 * 吊销API密钥
 */
export function revokeApiKey(keyId: string) {
  return http.post(`/api-keys/${keyId}/revoke`)
}
